import { Autocomplete, Box } from "@mui/material";
import { useState } from "react";
import { TextField, Typography } from "@platform/design-system";

import { RequestCarrierDialog } from "./RequestCarrierDialog";

interface SelectCarriersProps {
  carriers: string[];
  selectedCarrier: string | null;
  onCarrierChange: (carrier: string | null) => void;
  allowRequestNewCarrier?: boolean;
  disableRemove?: boolean;
}

const MIN_CHAR = 3;
const REQUEST_ADDITION_PREFIX = "__request__";

const RequestAdditionalCarrierOption = ({
  carrierName,
}: {
  carrierName: string;
}): React.JSX.Element => (
  <Box>
    <Typography variant="body-medium">
      <strong>Request addition of &ldquo;{carrierName}&rdquo; carrier</strong>
    </Typography>
    <Typography variant="body-medium" color="textSecondary">
      &ldquo;{carrierName}&rdquo; was not found in the database. Select this
      option to submit a request to add a new carrier. Requests are typically
      reviewed and processed within 24 hours.
    </Typography>
  </Box>
);

export function SelectCarriers({
  carriers,
  selectedCarrier,
  onCarrierChange,
  allowRequestNewCarrier,
  disableRemove,
}: SelectCarriersProps): React.JSX.Element {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestedCarrierName, setRequestedCarrierName] = useState("");
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <Typography variant="body-large" mt={2} mb={0.5}>
        Search Carriers
      </Typography>
      <Autocomplete
        options={carriers}
        value={selectedCarrier}
        inputValue={inputValue}
        open={open}
        freeSolo={!!allowRequestNewCarrier}
        disabled={!!disableRemove}
        disableClearable={!!disableRemove}
        noOptionsText="No carriers found"
        onOpen={() => {
          if (inputValue.length >= MIN_CHAR) setOpen(true);
        }}
        onClose={() => setOpen(false)}
        onFocus={() => setError(null)}
        onInputChange={(_, val, reason) => {
          setInputValue(val);
          setOpen(val.length >= MIN_CHAR);
          if (reason === "input") setError(null);
        }}
        onBlur={() => {
          if (
            inputValue &&
            !selectedCarrier &&
            !carriers.some((c) => c.toLowerCase() === inputValue.toLowerCase())
          ) {
            setError(`"${inputValue}" is not a valid carrier`);
          }
        }}
        filterOptions={(options, state) => {
          if (state.inputValue.length < MIN_CHAR) return [];

          const filtered = options.filter((option) =>
            option.toLowerCase().includes(state.inputValue.toLowerCase()),
          );

          const exactMatch = options.some(
            (o) => o.toLowerCase() === state.inputValue.toLowerCase(),
          );

          if (!exactMatch && allowRequestNewCarrier) {
            filtered.push(`${REQUEST_ADDITION_PREFIX}${state.inputValue}`);
          }

          return filtered;
        }}
        onChange={(_, val) => {
          if (
            typeof val === "string" &&
            val.startsWith(REQUEST_ADDITION_PREFIX)
          ) {
            const carrierName = val.replace(REQUEST_ADDITION_PREFIX, "");
            onCarrierChange(null);
            setInputValue("");
            setRequestedCarrierName(carrierName);
            setRequestDialogOpen(true);
          } else if (val === null || carriers.includes(val)) {
            setError(null);
            onCarrierChange(val);
          } else if (typeof val === "string") {
            setError(`"${val}" is not a valid carrier`);
          }
        }}
        renderOption={(props, option) => {
          if (option.startsWith(REQUEST_ADDITION_PREFIX)) {
            const carrierName = option.replace(REQUEST_ADDITION_PREFIX, "");
            return (
              <Box component="li" {...props} key={option}>
                <RequestAdditionalCarrierOption carrierName={carrierName} />
              </Box>
            );
          }

          return (
            <Box component="li" {...props} key={option}>
              <Typography variant="body-medium">{option}</Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Carrier Name..."
            helperText={error ?? `min ${MIN_CHAR} characters`}
            error={!!error}
            size="sm"
          />
        )}
      />
      {requestDialogOpen && allowRequestNewCarrier && (
        <RequestCarrierDialog
          carrierName={requestedCarrierName}
          onClose={() => setRequestDialogOpen(false)}
        />
      )}
    </>
  );
}
